import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Row } from 'react-bootstrap';
import api from '../services/api';
import Swal from 'sweetalert2';
import DOMPurify from 'dompurify';

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters')
    .matches(/^[a-zA-Z\s]*$/, 'Only letters and spaces allowed'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .max(255, 'Email is too long'),
  state_id: Yup.string().required('State is required'),
  phone: Yup.string()
    .matches(/^[0-9+\-\s()]*$/, 'Phone number is invalid')
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number is too long')
    .required('Phone number is required'),
  comment: Yup.string()
    .required('Comment is required')
    .min(10, 'Comment must be at least 10 characters')
    .max(1000, 'Comment cannot exceed 1000 characters')
    .matches(/^[a-zA-Z0-9\s.,!?'-]*$/, 'Invalid characters in comment'),
});

const CommentForm = ({ blogId, category, slug, states = [], onCommentSuccess }) => {
  const initialValues = {
    name: '',
    email: '',
    state_id: '',
    phone: '',
    comment: '',
  };

  const [isCooldown, setIsCooldown] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    // Check if reCAPTCHA is loaded
    if (!executeRecaptcha) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'reCAPTCHA not loaded. Please refresh the page and try again.',
        timer: 3000,
        showConfirmButton: false,
      });
      setSubmitting(false);
      return;
    }

    Swal.fire({
      title: 'Submitting...',
      text: 'Please wait while we process your comment.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Execute reCAPTCHA with error handling
      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha('submit_comment');
        if (!recaptchaToken) {
          throw new Error('reCAPTCHA token is empty');
        }
      } catch {
        throw new Error('Failed to verify reCAPTCHA. Please try again.');
      }

      const sanitizedValues = {
        name: DOMPurify.sanitize(values.name),
        email: values.email.trim(),
        state_id: values.state_id || null,
        phone: DOMPurify.sanitize(values.phone) || null,
        comment: DOMPurify.sanitize(values.comment),
        blog_id: blogId,
        category,
        slug,
        recaptchaToken,
      };

      const response = await api.post('/web-content/create-blog-comment', sanitizedValues);
      Swal.close();

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: DOMPurify.sanitize(response.data.message || 'Comment submitted successfully!'),
        timer: 3000,
        showConfirmButton: false,
      });

      // Find state name for the submitted state_id
      const state = states.find((s) => s.state_id === parseInt(values.state_id));

      // Call onCommentSuccess with the new comment data
      if (onCommentSuccess) {
        onCommentSuccess({
          ...sanitizedValues,
          state_name: state?.full_name || '',
        });
      }

      setStatus({ success: response.data.message || 'Comment submitted successfully!' });
      resetForm();

      setIsCooldown(true);
      setTimeout(() => {
        setIsCooldown(false);
      }, 5000);
    } catch (error) {
      Swal.close();

      const errorMessage =
        error.message || 'An error occurred while submitting the comment';

      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: DOMPurify.sanitize(errorMessage),
        timer: 3000,
        showConfirmButton: false,
      });

      setStatus({ error: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-form">
      <div className="comment-heading">
        <h3 className="blog-font mb-0">Leave a comment</h3>
        <p className="mb-0 leave-text">
          Your email address will not be published. Required fields are marked*
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form>
            <Row>
              <div className="form-group col-md-6">
                <Field
                  type="text"
                  name="name"
                  placeholder="Name *"
                  className="form-control"
                  disabled={isSubmitting || isCooldown}
                  maxLength={50}
                  pattern="[A-Za-z\s]*"
                  title="Only letters and spaces allowed"
                />
                <ErrorMessage name="name" component="div" className="text-danger" />
              </div>

              <div className="form-group col-md-6">
                <Field
                  type="email"
                  name="email"
                  placeholder="Email *"
                  className="form-control"
                  disabled={isSubmitting || isCooldown}
                  maxLength={255}
                />
                <ErrorMessage name="email" component="div" className="text-danger" />
              </div>

              <div className="form-group col-md-6">
                <Field
                  type="tel"
                  name="phone"
                  placeholder="Phone *"
                  className="form-control"
                  disabled={isSubmitting || isCooldown}
                  maxLength={20}
                  inputMode="tel"
                  autoComplete="tel"
                />
                <ErrorMessage name="phone" component="div" className="text-danger" />
              </div>

              <div className="form-group col-md-6">
                <Field
                  as="select"
                  name="state_id"
                  className="form-select"
                  placeholder="Select State"
                  disabled={isSubmitting || isCooldown}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.state_id} value={state.state_id}>
                      {state.full_name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="state_id" component="div" className="text-danger" />
              </div>

              <div className="form-group col-md-12 text-group">
                <Field
                  as="textarea"
                  name="comment"
                  className="form-control"
                  placeholder="Comment *"
                  disabled={isSubmitting || isCooldown}
                  maxLength={1000}
                />
                <ErrorMessage name="comment" component="div" className="text-danger" />
              </div>

              <div className="form-group mb-0 col-md-12 text-right comment-btn">
                <button
                  type="submit"
                  className="contact-btn submit-btn"
                  disabled={isSubmitting || isCooldown}
                >
                  {isSubmitting
                    ? 'Submitting...'
                    : isCooldown
                      ? 'Please wait...'
                      : 'Submit Comment'}
                </button>
              </div>
            </Row>

            {status?.success && (
              <div className="text-success text-center mt-2">{status.success}</div>
            )}
            {status?.error && (
              <div className="text-danger text-center mt-2">{status.error}</div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CommentForm;
